import { FC, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "@/components/trackedService/createTrackedServiceModal.module.css";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Checkbox,
  FormControlLabel,
  Modal,
  Typography,
} from "@mui/material";
import GenericButton from "@/components/forms/inputs/button/genericButton";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { SubjectSearchSelect } from "@/components/subjectSearch/subjectSearchSelect";
import { useSubjectSearch } from "@/helper/customHooks/useSubjectSearch";
import { InputWithRef } from "@/components/forms/inputsWithRef/inputWithRef";
import { TextAreaWithRef } from "@/components/forms/inputsWithRef/textAreaWithRef";
import dayjs, { Dayjs } from "dayjs";
import { useSelector } from "react-redux";
import { RootState } from "@/components/state/store";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import GenericDateRange from "@/components/dateTime/genericDateRange";
import { RadioButtonsWithRef } from "@/components/forms/inputsWithRef/radioButtonsWithRef";
import { useModalDismiss } from "@/helper/customHooks/useModalDismiss";
import { KAER_COMPANY_ID } from "@/helper/systemIds";
import useFocusFirstInvalidField from "@/helper/customHooks/useFocusFirstInvalidField";

interface CreateTrackedServiceModalProps {
  modalOpen: boolean;
  onClose: () => void;
  selectedTableRows: any[];
  handleSubmitFormData: (data: any) => void;
}

type TrackedServiceFormValues = {
  description: string;
  info_ops: string;
  info_finance: string;
  category: any;
  tracked_service_type: any;
  on_site: boolean;
  building: any;
  actual_duration: number | string;
  estimated_duration: number | string;
  price: any;
  vat: any;
  company_subject_set: [] | any;
  is_exclusion_list: boolean;
  include_children: boolean;
  parents_only: boolean;
  use_manager_subjects: boolean;
  tracked_service_template: string;
  executed_by: string | any;
  executed_on: string | Date | Dayjs;
  batch: boolean;
};
const CreateTrackedServiceModal: FC<CreateTrackedServiceModalProps> = ({
  modalOpen,
  onClose,
  selectedTableRows,
  handleSubmitFormData,
}) => {
  const { t } = useTranslation();

  const loginStateForExecutedBy = useSelector(
    (state: RootState) => state.login,
  );
  const {
    control,
    setValue,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<TrackedServiceFormValues>({
    shouldFocusError: false,
    defaultValues: {
      description: "",
      info_ops: "",
      info_finance: "",
      category: "",
      on_site: false,
      building: "",
      actual_duration: 0,
      estimated_duration: 0,
      tracked_service_type: "",
      price: 0,
      vat: 19,
      company_subject_set: [],
      is_exclusion_list: false,
      include_children: false,
      parents_only: false,
      use_manager_subjects: false,
      tracked_service_template: "",
      executed_by: {
        value: loginStateForExecutedBy.subjectId,
        label: `${loginStateForExecutedBy?.firstname} ${loginStateForExecutedBy.lastname}`,
      },
      executed_on: dayjs.utc().startOf("day").toISOString(),
      batch: true,
    },
  });
  const parentsOnlySelected = watch("parents_only");
  const includeChildrenSelected = watch("include_children");
  const isExclusionList = watch("is_exclusion_list");
  const { registerValidationTarget, focusFirstInvalidField } =
    useFocusFirstInvalidField<TrackedServiceFormValues>({
      fieldOrder: [
        "tracked_service_template",
        "actual_duration",
        "executed_on",
        "executed_by",
      ],
    });

  useEffect(() => {
    if (modalOpen) {
      reset();
    }
  }, [modalOpen, reset]);
  const onSubmit = (data: any) => {
    const tempCompanySubjectSet =
      selectedTableRows.length > 0
        ? selectedTableRows
            .map((row: any) => row.subject?.id || row.subject_id)
            .filter(Boolean)
        : [];

    const submitData: Partial<TrackedServiceFormValues> = {
      company_subject_set: tempCompanySubjectSet,
      tracked_service_template: data?.tracked_service_template?.value,
      actual_duration: data?.actual_duration,
      info_ops: data?.info_ops,
      executed_on: data?.executed_on,
      executed_by: data.executed_by?.value,
      category: data?.tracked_service_template?.category,
      estimated_duration: data?.tracked_service_template?.estimated_duration,
      price: data?.tracked_service_template?.price,
      tracked_service_type:
        data?.tracked_service_template?.tracked_service_type,
      on_site: data.on_site === "yes" || data.on_site === true,

      is_exclusion_list: data.is_exclusion_list,
      include_children: data.include_children,
      parents_only: data.parents_only,
      batch: true,
    };
    handleSubmitFormData(submitData);
  };

  const getTranslationKey = () => {
    const count = selectedTableRows.length;

    if (count === 0) {
      if (parentsOnlySelected)
        return "no_companies_selected_create_trackedservice_for_all_parents_only";
      return "no_companies_selected_create_trackedservice_for_all";
    }

    if (isExclusionList) {
      if (parentsOnlySelected) {
        return "create_trackedservice_for_all_companies_except_selected_parents_only";
      }
      if (includeChildrenSelected) {
        return "create_trackedservice_for_all_companies_except_selected_include_children";
      }
      return "create_trackedservice_for_all_companies_except_selected_companies";
    }

    if (parentsOnlySelected && !isExclusionList) {
      return "create_trackedservice_for_selected_companies_parents_only";
    }

    if (includeChildrenSelected) {
      return "create_trackedservice_for_all_selected_companies_include_children";
    }

    return "create_trackedservice_for_selected_companies";
  };

  const { fetchSubjects } = useSubjectSearch({
    subdomain: "admin",
    entity: "trackedServiceTemplate",
  });

  useModalDismiss(modalOpen, onClose);

  return (
    <Modal
      open={modalOpen}
      onClose={onClose}
      disableAutoFocus
    >
      <Box className={styles.modalContentBoxWrapper}>
        <Typography variant="h6" component="h2" gutterBottom align="center">
          {t("trackedservice_create")}
        </Typography>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit, (errors) => {
            focusFirstInvalidField(errors);
          })}
        >
          <Box className={styles.formContent}>
            <Controller
              name="tracked_service_template"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field: { onChange, value, name } }) => (
                <div ref={registerValidationTarget("tracked_service_template")}>
                  <FormFieldLabel
                    label={t("trackedservice_template")}
                    htmlFor={name}
                    mandatory
                  />
                  <SubjectSearchSelect
                    value={value}
                    name={name}
                    loadOptions={(input: any) =>
                      fetchSubjects(input, {
                        filterParameter: {
                          internal_name__icontains: input,
                        },
                        mapResponseToOptions: (data) =>
                          data.results.map((item: any) => ({
                            value: item.id,
                            label: `${item.internal_name}`,
                            category: item.category.id,
                            estimated_duration: item.estimated_duration,
                            price: item.price,
                            tracked_service_type: item.tracked_service_type.id,
                          })),
                      })
                    }
                    cacheOptions={false}
                    onChange={onChange}
                    isClearable={true}
                    placeholder={t("choose_template")}
                  />
                </div>
              )}
            />
            {errors.tracked_service_template && (
              <Typography color="error" variant="caption">
                {errors.tracked_service_template.message}
              </Typography>
            )}
            <Controller
              name="actual_duration"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field }) => (
                <div ref={registerValidationTarget("actual_duration")}>
                  <InputWithRef
                    {...field}
                    name={field.name}
                    onChange={field.onChange}
                    label={t("trackedservice_actual_duration")}
                    ref={field.ref}
                    value={field.value}
                    required
                    clearable
                  />
                </div>
              )}
            />
            {errors.actual_duration && (
              <Typography color="error" variant="caption">
                {errors.actual_duration.message}
              </Typography>
            )}
            <Controller
              name="info_ops"
              control={control}
              render={({ field }) => (
                <>
                  <TextAreaWithRef
                    label={t("trackedservice_info_ops")}
                    name="info_ops"
                    onChange={field.onChange}
                    ref={field.ref}
                    value={field.value}
                  />
                </>
              )}
            />
            {errors.info_ops && (
              <Typography color="error" variant="caption">
                {errors.info_ops.message}
              </Typography>
            )}
            <Controller
              name="executed_on"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field }) => {
                const handleDateChange = (e: any) => {
                  if (!e) {
                    field.onChange("");
                    return;
                  }
                  const formattedValue = dayjs
                    .utc(dayjs(e).format("YYYY-MM-DD"))
                    .startOf("day")
                    .toISOString();

                  field.onChange(formattedValue);
                };
                return (
                  <div ref={registerValidationTarget("executed_on")}>
                    <FormControlLabel
                      labelPlacement="top"
                      disableTypography
                      sx={{
                        display: "flex",
                        margin: "0",
                        alignItems: "start",
                        width: "100%",
                      }}
                      label={
                        <section>
                          {t("executed_on")}
                          <span style={{ color: "red" }}>*</span>
                        </section>
                      }
                      control={
                        <GenericDateRange
                          selectionMode="single"
                          className={styles.datePicker}
                          value={field.value ? dayjs(field.value) : null}
                          onChange={handleDateChange}
                          onClear={() => {
                            setValue("executed_on", "");
                          }}
                        />
                      }
                    />
                  </div>
                );
              }}
            />
            {errors.executed_on && (
              <Typography color="error" variant="caption">
                {errors.executed_on.message}
              </Typography>
            )}
            <Controller
              name="executed_by"
              control={control}
              rules={{ required: t("field_required") as string }}
              render={({ field: { onChange, value, name } }) => (
                <div ref={registerValidationTarget("executed_by")}>
                  <FormFieldLabel
                    label={t("executed_by")}
                    htmlFor={name}
                    mandatory
                  />
                  <SubjectSearchSelect
                    value={value}
                    name={name}
                    loadOptions={(input: any) =>
                      fetchSubjects(input, {
                        filterParameter: {
                          name__icontains: input,
                        },
                        companyIdForFiltering: KAER_COMPANY_ID,
                        entity: "user",
                        mapResponseToOptions: (data) =>
                          data.results.map((item: any) => ({
                            value: item.subject_id,
                            label: `${item.name}`,
                          })),
                      })
                    }
                    cacheOptions={false}
                    onChange={onChange}
                    isClearable={true}
                    placeholder={t("choose_template")}
                  />
                </div>
              )}
            />
            {typeof errors.executed_by?.message === "string" && (
              <Typography color="error" variant="caption">
                {errors.executed_by.message}
              </Typography>
            )}
            <Controller
              name="on_site"
              control={control}
              render={({ field: { onChange, ref, value } }) => (
                <RadioButtonsWithRef
                  key={String(value)}
                  name="on_site"
                  label={t("trackedservice_on_site")}
                  onChange={onChange}
                  ref={ref}
                  defaultValue={value}
                />
              )}
            />
            <Box className={styles.checkboxWrapper}>
              <Controller
                name="is_exclusion_list"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("is_exclusion_list")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          disabled={selectedTableRows.length === 0}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="include_children"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("include_child_companies")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={
                            parentsOnlySelected ||
                            selectedTableRows.length === 0
                          }
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
              <Controller
                name="parents_only"
                control={control}
                render={({ field }) => (
                  <>
                    <FormControlLabel
                      label={t("only_parent_companies")}
                      control={
                        <Checkbox
                          {...field}
                          value={field.value}
                          checked={field.value}
                          onChange={(event) =>
                            field.onChange(event.target.checked)
                          }
                          disabled={includeChildrenSelected}
                        />
                      }
                      className={styles.modalCheckbox}
                    />
                  </>
                )}
              />
            </Box>
          </Box>
          <div className={styles.infoContainer}>
            <InfoOutlinedIcon />
            <p className={styles.infoText}>
              {t(getTranslationKey(), { count: selectedTableRows.length })}
            </p>
          </div>
          <Box className={styles.buttonWrapper}>
            <GenericButton variant="outlined" onClick={onClose}>
              {t("cancel")}
            </GenericButton>
            <GenericButton variant="contained" type="submit">
              {t("save")}
            </GenericButton>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
export default CreateTrackedServiceModal;
